import {
  useMutation,
  useQueryClient,
  UseMutationResult,
} from "@tanstack/react-query";
import { voiceAgentService, VoiceAgent } from "@/services/voiceAgentService";
import { toast } from "sonner";

interface SaveCredentialsParams {
  agentId: string;
  phone_number: string;
  twilio_account_sid: string;
  twilio_auth_token: string;
}

interface ConnectPhoneParams {
  agentId: string;
  phoneNumber: string;
  twilioAccountSid: string;
  twilioAuthToken: string;
}

interface DisconnectPhoneParams {
  agentId: string;
}

interface DisconnectPhoneResult {
  data: VoiceAgent | null;
  vapiDeleteSuccess?: boolean;
  vapiError?: string;
  message?: string;
  error: Error | null;
}

interface UsePhoneNumberMutationsReturn {
  saveCredentials: UseMutationResult<VoiceAgent, Error, SaveCredentialsParams>;
  connectPhone: UseMutationResult<VoiceAgent, Error, ConnectPhoneParams>;
  disconnectPhone: UseMutationResult<VoiceAgent, Error, DisconnectPhoneParams>;
  isLoading: boolean;
}

/**
 * Custom hook for phone number mutations
 * Handles connecting, disconnecting, and saving phone number credentials
 * with automatic cache invalidation and error handling
 *
 * @example
 * ```tsx
 * const { connectPhone, disconnectPhone, isLoading } = usePhoneNumberMutations();
 *
 * // Connect phone number
 * connectPhone.mutate({
 *   agentId: "123",
 *   phoneNumber: "+1234567890",
 *   twilioAccountSid: "ACxxx",
 *   twilioAuthToken: "xxx"
 * });
 * ```
 */
export function usePhoneNumberMutations(): UsePhoneNumberMutationsReturn {
  const queryClient = useQueryClient();

  // Mutation for saving Twilio credentials to database
  const saveCredentials = useMutation({
    mutationFn: async (params: SaveCredentialsParams) => {
      const { data, error } = await voiceAgentService.updateVoiceAgent(
        params.agentId,
        {
          phone_number: params.phone_number,
          twilio_account_sid: params.twilio_account_sid,
          twilio_auth_token: params.twilio_auth_token,
        },
      );
      if (error) throw error;
      if (!data) throw new Error("No agent data returned");
      return data;
    },
    onSuccess: () => {
      // Invalidate voice agents cache to trigger refetch
      queryClient.invalidateQueries({ queryKey: ["voice-agents"] });
      toast.success("Twilio credentials saved successfully!");
    },
    onError: (error: Error) => {
      toast.error(`Save failed: ${error.message}`);
    },
  });

  // Mutation for connecting phone number to VAPI
  const connectPhone = useMutation({
    mutationFn: async (params: ConnectPhoneParams) => {
      const { data, error } = await voiceAgentService.connectTwilioPhoneNumber(
        params.agentId,
        params.phoneNumber,
        params.twilioAccountSid,
        params.twilioAuthToken,
      );
      if (error) throw error;
      if (!data?.voice_agent) throw new Error("No voice agent returned");
      return data.voice_agent;
    },
    onSuccess: () => {
      // Invalidate voice agents cache to trigger refetch
      queryClient.invalidateQueries({ queryKey: ["voice-agents"] });
      toast.success("Phone number connected successfully!");
    },
    onError: (error: Error) => {
      toast.error(`Connection failed: ${error.message}`);
    },
  });

  // Mutation for disconnecting phone number from VAPI
  const disconnectPhone = useMutation({
    mutationFn: async (params: DisconnectPhoneParams) => {
      const result = await voiceAgentService.disconnectPhoneNumber(
        params.agentId,
      );
      if (result.error) throw result.error;
      if (!result.data) throw new Error("No agent data returned");
      return result.data;
    },
    onSuccess: () => {
      // Invalidate voice agents cache to trigger refetch
      queryClient.invalidateQueries({ queryKey: ["voice-agents"] });

      // Note: We can't access vapiDeleteSuccess here as it's not part of VoiceAgent
      // The toast is already shown in the service layer
      toast.success("Phone number disconnected successfully!");
    },
    onError: (error: Error) => {
      toast.error(`Disconnection failed: ${error.message}`);
    },
  });

  return {
    saveCredentials,
    connectPhone,
    disconnectPhone,
    // Computed loading state - true if any mutation is pending
    isLoading:
      saveCredentials.isPending ||
      connectPhone.isPending ||
      disconnectPhone.isPending,
  };
}
